import React, { useMemo } from "react";
import { Box, Typography, Paper, Skeleton, Alert, Stack, Grid } from "@mui/material";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { useUploaddataqutaQuery } from "../store/services/createDbApi";

// ---- config ---------------------------------------------------------------

const USED_COLOR = "#2563EB";
const FREE_COLOR = "#E2E8F0";

// Broad keyword matching so this works regardless of your exact API field
// names (usedSpace, used_bytes, storageUsed, consumed... all match "used").
const USED_HINTS = ["used", "consum", "occupi"];
const TOTAL_HINTS = ["total", "limit", "quota", "capacity", "max"];

const isNumeric = (v) => typeof v === "number" && !Number.isNaN(v);

const findByHints = (obj, hints, exclude = []) => {
  const entry = Object.entries(obj).find(
    ([k, v]) => isNumeric(v) && !exclude.includes(k) && hints.some((h) => k.toLowerCase().includes(h))
  );
  return entry || null;
};

const prettyLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^\w/, (c) => c.toUpperCase());

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes}`;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

// ---- states -----------------------------------------------------------

const LoadingState = () => (
  <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
    <Skeleton variant="circular" width={160} height={160} />
  </Box>
);

const EmptyState = () => (
  <Box
    sx={{
      mt: 2,
      py: 3,
      textAlign: "center",
      borderRadius: 1,
      border: "1px dashed",
      borderColor: "divider",
      color: "text.secondary",
    }}
  >
    <Typography variant="body2">No data available</Typography>
  </Box>
);

// ---- storage donut (used shown against a known total/limit) --------------

const StorageDonut = ({ usedLabel, used, totalLabel, total }) => {
  const free = Math.max(total - used, 0);
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;
  const chartData = [
    { name: usedLabel, value: used },
    { name: "Remaining", value: free },
  ];

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 1 }}>
      <Stack direction="row" spacing={3} alignItems="center" justifyContent="center" flexWrap="wrap">
        <Box sx={{ position: "relative", width: 160, height: 160, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                innerRadius={58}
                outerRadius={78}
                startAngle={90}
                endAngle={-270}
                paddingAngle={1}
                stroke="none"
              >
                <Cell fill={USED_COLOR} />
                <Cell fill={FREE_COLOR} />
              </Pie>
              <Tooltip formatter={(value, name) => [formatBytes(value), name]} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
            </PieChart>
          </ResponsiveContainer>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1 }}>{pct}%</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5 }}>
              used
            </Typography>
          </Box>
        </Box>

        <Stack spacing={1.25} sx={{ minWidth: 140 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: USED_COLOR, flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 11 }}>
                {prettyLabel(usedLabel)}
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{formatBytes(used)}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: FREE_COLOR, flexShrink: 0 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 11 }}>
                Remaining
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{formatBytes(free)}</Typography>
            </Box>
          </Stack>
          <Box sx={{ pt: 0.5, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {prettyLabel(totalLabel)}: {formatBytes(total)}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
};

// ---- fallback: clean generic view of whatever fields actually exist ------

const GenericFields = ({ obj }) => {
  const entries = Object.entries(obj).filter(([, v]) => typeof v !== "object" || v === null);

  return (
    <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
      {entries.map(([k, v]) => (
        <Grid item xs={6} sm={4} key={k}>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 }}
              noWrap
            >
              {prettyLabel(k)}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 15, mt: 0.25 }} noWrap>
              {isNumeric(v) ? formatBytes(v) : String(v ?? "—")}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

// ---- main component -----------------------------------------------------

const DataRecord = () => {
  const { data, isLoading, isError, error } = useUploaddataqutaQuery();
  const raw = data?.data;
  const source = Array.isArray(raw) ? raw[0] : raw;

  const storagePair = useMemo(() => {
    if (!source || typeof source !== "object") return null;
    const usedEntry = findByHints(source, USED_HINTS);
    const totalEntry = findByHints(source, TOTAL_HINTS, usedEntry ? [usedEntry[0]] : []);
    if (usedEntry && totalEntry && totalEntry[1] > 0) {
      return { usedLabel: usedEntry[0], used: usedEntry[1], totalLabel: totalEntry[0], total: totalEntry[1] };
    }
    return null;
  }, [source]);

  const hasAnyData = source && typeof source === "object" && Object.keys(source).length > 0;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Storage Usage
      </Typography>

      {isLoading && <LoadingState />}

      {isError && (
        <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
          {error?.data?.message || error?.message || "Failed to load storage details."}
        </Alert>
      )}

      {!isLoading && !isError && !hasAnyData && <EmptyState />}

      {!isLoading && !isError && hasAnyData && storagePair && <StorageDonut {...storagePair} />}

      {!isLoading && !isError && hasAnyData && !storagePair && <GenericFields obj={source} />}
    </Box>
  );
};

export default DataRecord;